/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect } from 'react';
import { Input, Box, FormControl, FormLabel, FormHelperText, InputGroup, InputRightElement, Spinner, Tooltip, Text, Flex } from '@chakra-ui/react';
import { Field, useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import { gql, useLazyQuery } from '@apollo/client';
import { CheckCircleIcon } from '@chakra-ui/icons';
import './animation.css';

const Q_PROPOSAL = gql`
	query get($proposalNumber: Float!) {
		getProposalLinkableByProposalNumber(proposalNumber: $proposalNumber) {
			id
			subject
			no
			createdAt
		}
	}
`;

const ProposalLinkableInputField = ({ name = null, label = null, required = false, disabled = false }) => {
	const { t } = useTranslation();
	const { values, setFieldValue } = useFormikContext();
	const [loadData, { loading, data }] = useLazyQuery(Q_PROPOSAL);

	useEffect(() => {
		const timeOut = setTimeout(() => {
			if (values[name]?.proposalNumber) {
				loadData({ variables: { proposalNumber: parseInt(values[name]?.proposalNumber) } });
			}
		}, 800);
		return () => clearTimeout(timeOut);
	}, [values[name]?.proposalNumber]);

	useEffect(() => {
		setFieldValue(name, { ...values[name], proposal: data?.getProposalLinkableByProposalNumber });
	}, [data]);

	return (
		<Field name={name}>
			{({ form, field, meta }) => (
				<Box>
					<FormControl id={name}>
						<FormLabel>
							{t(label)}
							{required && <span style={{ color: 'red' }}>*</span>}
						</FormLabel>
						<InputGroup size="md">
							<Input
								{...field}
								value={field?.value?.proposalNumber || ''}
								id={name}
								type="number"
								placeholder={t('Proposal Number')}
								size="md"
								disabled={disabled}
								onChange={(e) => form?.setFieldValue(name, { proposalNumber: e?.target?.value, proposal: null })}
							/>
							<InputRightElement>
								<Spinner
									size="sm"
									color="green.500"
									borderWidth="3px"
									animationDuration="0.8s"
									display={loading ? 'block' : 'none'}
									animation={loading ? 'fade-in 0.1s ease-in-out' : 'fade-out 0.1s ease-in-out'}
								/>
								<Box
									display={!loading && field?.value?.proposalNumber && field?.value?.proposal ? 'block' : 'none'}
									animation={
										!loading && field?.value?.proposalNumber && field?.value?.proposal
											? 'fade-in 0.4s ease-in-out'
											: 'fade-out 0.4s ease-in-out'
									}
								>
									<Tooltip
										label={
											<Flex direction="column" padding={2}>
												<Flex>
													<Text fontWeight="700" color="white" noOfLines={1}>
														{t('Proposal Number')}:&nbsp;
													</Text>
													<Text flex={1} fontWeight="400" color="white" noOfLines={1}>
														{field?.value?.proposal?.no}
													</Text>
												</Flex>
												<Flex>
													<Text fontWeight="700" color="white" noOfLines={1}>
														{t('Subject')}:&nbsp;
													</Text>
													<Text flex={1} fontWeight="400" color="white" noOfLines={1}>
														{field?.value?.proposal?.subject}
													</Text>
												</Flex>
											</Flex>
										}
									>
										<CheckCircleIcon width="18px" height="18px" color="green.500" />
									</Tooltip>
								</Box>
							</InputRightElement>
						</InputGroup>
						{meta.touched && meta.error && (
							<FormHelperText id={`error-message-${name}`} color="red.400" fontSize="12px" mt="4px">
								{t(label)} {t('is')} {t(meta.error)}
							</FormHelperText>
						)}
					</FormControl>
				</Box>
			)}
		</Field>
	);
};

export default ProposalLinkableInputField;
